import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Icon, Popover, RoleChip, SearchInput, Spinner } from '@w7/ui'
import { useSearchHierarchyNew } from '@/api/hooks/member/useSearchHierarchyNew'
import { useReportHierarchyTree } from '@/api/hooks/reports/useReportScope'
import { count } from '@/lib/format'
import { ROLE_LABEL, levelToRole } from '@/features/hierarchy/hierarchy'
import { idsEqual, type HierarchyNode } from '@/features/hierarchy/hierarchy'
import type { DateRange, ReportAccount } from '@/features/reports/types'

export type MemberSource = 'settled' | 'downline' | 'hierarchy'

const DOWNLINE_QUERY_SIZE = 2000

export interface MemberPickerProps {
  selected: string[]
  onChange: (next: string[]) => void
  source: MemberSource
  range: DateRange
  rootId: string | null
  allLabel?: string
  label?: string
}

interface Option {
  account: ReportAccount
  parent: string | null
}

function nodeToReportAccount(node: HierarchyNode, nodes: HierarchyNode[]): ReportAccount {
  return {
    id: node.id,
    username: node.username ?? node.name,
    displayName: node.name,
    role: levelToRole(node.level),
    parentId: node.parentId,
    path: [],
    memberCount: nodes.filter((n) => idsEqual(n.parentId, node.id) && n.level === 6).length,
  }
}

export function MemberPicker({
  selected,
  onChange,
  source,
  allLabel = 'All members',
  label = 'Members',
}: MemberPickerProps) {
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const caption = selected.length === 0 ? allLabel : `${count(selected.length)} selected`

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="w7-rangetrigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${caption}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="users" size={16} />
        <span className="w7-rangetrigger-text">
          <span className="w7-rangetrigger-preset">{label}</span>
          <span className="w7-rangetrigger-sub">{caption}</span>
        </span>
        <Icon name="chevron-down" size={14} />
      </button>

      <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchor} label={label} width={380}>
        {source === 'hierarchy' ? (
          <HierarchyOptions selected={selected} onChange={onChange} open={open} />
        ) : source === 'settled' ? (
          <TreeMemberOptions selected={selected} onChange={onChange} membersOnly />
        ) : (
          <TreeMemberOptions selected={selected} onChange={onChange} />
        )}
      </Popover>
    </>
  )
}

function TreeMemberOptions({
  selected,
  onChange,
  membersOnly = false,
}: {
  selected: string[]
  onChange: (next: string[]) => void
  membersOnly?: boolean
}) {
  const treeQuery = useReportHierarchyTree()
  const nodes = (treeQuery.data?.nodes ?? []).slice(0, DOWNLINE_QUERY_SIZE)

  const options = useMemo<Option[]>(() => {
    const filtered = membersOnly ? nodes.filter((n) => n.level === 6) : nodes
    const byId = new Map(nodes.map((n) => [n.id, n]))
    return filtered.map((node) => ({
      account: nodeToReportAccount(node, nodes),
      parent: node.parentId
        ? (byId.get(node.parentId)?.username ?? byId.get(node.parentId)?.name ?? null)
        : null,
    }))
  }, [membersOnly, nodes])

  return (
    <OptionList
      options={options}
      loading={treeQuery.isLoading}
      selected={selected}
      onChange={onChange}
      emptyMessage={
        membersOnly
          ? 'No members in your downline to filter to.'
          : 'You hold no accounts beneath you yet.'
      }
      searchLabel={membersOnly ? 'Filter members' : 'Filter counterparties'}
    />
  )
}

function HierarchyOptions({
  selected,
  onChange,
  open,
}: {
  selected: string[]
  onChange: (next: string[]) => void
  open: boolean
}) {
  const [needle, setNeedle] = useState('')
  const [debouncedNeedle, setDebouncedNeedle] = useState('')
  const hierarchy = useSearchHierarchyNew(debouncedNeedle, open, { prefetchOnEmpty: true })

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedNeedle(needle), 100)
    return () => window.clearTimeout(timer)
  }, [needle])

  const suggestions = useMemo(() => {
    const rows = hierarchy.data ?? []
    const text = needle.trim().toLowerCase()
    const matching = text
      ? rows.filter((username) => username.toLowerCase().includes(text))
      : rows
    return [...new Set([...selected, ...matching])]
  }, [hierarchy.data, needle, selected])

  const toggle = (username: string) =>
    onChange(
      selected.includes(username)
        ? selected.filter((item) => item !== username)
        : [...selected, username],
    )

  return (
    <>
      <SearchInput
        value={needle}
        onChange={setNeedle}
        label="Search hierarchy members"
        placeholder="Enter or select member name"
      />

      {hierarchy.isFetching && suggestions.length === selected.length ? (
        <span className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
          <Spinner size={14} />
          <span className="w7-dim" style={{ fontSize: 12 }}>
            Searching downline
          </span>
        </span>
      ) : hierarchy.isError ? (
        <p className="w7-scopenote">Could not load members from hierarchy.</p>
      ) : suggestions.length === 0 ? (
        <p className="w7-scopenote">
          {needle.trim() ? `No members match “${needle.trim()}”.` : 'No members found beneath you.'}
        </p>
      ) : (
        <div className="w7-pickerlist">
          {suggestions.map((username) => (
            <div key={username} className="w7-pickerrow">
              <Checkbox
                checked={selected.includes(username)}
                onChange={() => toggle(username)}
                label={`Filter to ${username}`}
              />
              <span className="w7-pickerrow-body">{username}</span>
            </div>
          ))}
        </div>
      )}

      <div className="w7-pickerfoot">
        <span>{count(selected.length)} selected</span>
        <span className="w7-spacer" />
        <Button
          variant="ghost"
          size="sm"
          icon="close"
          onClick={() => onChange([])}
          disabled={selected.length === 0}
        >
          Clear members
        </Button>
      </div>
    </>
  )
}

function OptionList({
  options,
  loading,
  selected,
  onChange,
  emptyMessage,
  searchLabel,
}: {
  options: Option[]
  loading: boolean
  selected: string[]
  onChange: (next: string[]) => void
  emptyMessage: string
  searchLabel: string
}) {
  const [needle, setNeedle] = useState('')
  const text = needle.trim().toLowerCase()
  const shown = options.filter(
    (option) =>
      !text ||
      option.account.username.toLowerCase().includes(text) ||
      option.account.displayName.toLowerCase().includes(text),
  )
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id])

  return (
    <>
      <SearchInput value={needle} onChange={setNeedle} label={searchLabel} placeholder={searchLabel} />
      {loading ? (
        <span className="w7-row"><Spinner size={14} /> Loading</span>
      ) : options.length === 0 ? (
        <p className="w7-scopenote">{emptyMessage}</p>
      ) : (
        <div className="w7-pickerlist">
          {shown.map((option) => (
            <div key={option.account.id} className="w7-pickerrow">
              <Checkbox
                checked={selected.includes(option.account.id)}
                onChange={() => toggle(option.account.id)}
                label={`Filter to ${option.account.username}`}
              />
              <span className="w7-pickerrow-body">
                {option.account.username}{' '}
                <RoleChip role={option.account.role} label={ROLE_LABEL[option.account.role]} />
                {option.parent ? <small className="w7-pickerrow-sub">under {option.parent}</small> : null}
              </span>
            </div>
          ))}
          {!shown.length ? <p className="w7-scopenote">Nothing matches “{needle.trim()}”.</p> : null}
        </div>
      )}
      <div className="w7-pickerfoot">
        <span>{count(selected.length)} selected</span>
        <Button variant="ghost" size="sm" icon="close" onClick={() => onChange([])} disabled={!selected.length}>
          Clear
        </Button>
      </div>
    </>
  )
}

